import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SessionData } from '~/cards/reducers/plpPhotoPreview';
import { updatePlpPhotoPreviewState } from '~/cards/actions/plpPhotoPreview';
import { getPLPPhotoPreviewState } from '~/cards/selectors/plpPhotoPreview';
import { isPhotoPreviewSupported, ZOLA_PLP_PHOTO_PREVIEW } from './constants';

export const useSessionStorageData = (
  inputCardType?: string,
  isDigital = false
): SessionData & { resetSessionData: () => void } => {
  const dispatch = useDispatch();
  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);

  const cardType = inputCardType && `${isDigital ? 'DIGITAL_' : ''}${inputCardType}`;

  const resetSessionData = useCallback(() => {
    if (!plpPhotoPreviewState?.sessionData?.initialized) {
      return;
    }
    const inUse =
      typeof plpPhotoPreviewState?.sessionData?.isInUseForCardType !== 'boolean'
        ? true
        : plpPhotoPreviewState?.sessionData?.isInUseForCardType;

    dispatch(
      updatePlpPhotoPreviewState({
        sessionData: {
          initialized: true,
          currentFlowUuid: null,
          currentUploadcareFile: null,
          isInUseForCardType: inUse,
        },
      })
    );
  }, [dispatch, plpPhotoPreviewState]);

  useEffect(() => {
    const sessionData = window.sessionStorage.getItem(ZOLA_PLP_PHOTO_PREVIEW);
    const currentPlpPhotoPreviewInUseByCardType =
      (typeof sessionData === 'string' && JSON.parse(sessionData)) || {};

    if (cardType && !plpPhotoPreviewState?.sessionData?.initialized) {
      if (currentPlpPhotoPreviewInUseByCardType[cardType]) {
        const { uploadcareFile = null, flowUuid = null } = currentPlpPhotoPreviewInUseByCardType[
          cardType
        ];

        if (isPhotoPreviewSupported(cardType)) {
          const inUse =
            typeof plpPhotoPreviewState?.sessionData?.isInUseForCardType !== 'boolean'
              ? true
              : plpPhotoPreviewState?.sessionData?.isInUseForCardType;

          dispatch(
            updatePlpPhotoPreviewState({
              sessionData: {
                initialized: true,
                currentUploadcareFile: uploadcareFile,
                currentFlowUuid: flowUuid,
                isInUseForCardType: inUse,
              },
            })
          );
        }
      } else {
        dispatch(
          updatePlpPhotoPreviewState({
            sessionData: {
              initialized: true,
              currentFlowUuid: plpPhotoPreviewState?.sessionData?.currentFlowUuid || null,
              currentUploadcareFile:
                plpPhotoPreviewState?.sessionData?.currentUploadcareFile || null,
              isInUseForCardType: false,
            },
          })
        );
      }
    }
  }, [dispatch, cardType, plpPhotoPreviewState]);

  return {
    initialized: plpPhotoPreviewState?.sessionData?.initialized || false,
    currentFlowUuid: plpPhotoPreviewState?.sessionData?.currentFlowUuid || null,
    currentUploadcareFile: plpPhotoPreviewState?.sessionData?.currentUploadcareFile || null,
    isInUseForCardType: plpPhotoPreviewState?.sessionData?.isInUseForCardType || false,
    resetSessionData,
  };
};
